import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Stack,
  Title,
  Text,
  Flex,
  Avatar,
  Rating,
  Button,
  Divider,
  Badge,
  Anchor,
  List,
  Loader,
  Center,
} from '@mantine/core';
import { isMobile } from 'react-device-detect';
import { useApplyToJobMutation } from '@worker/hooks/useApplyToJobMutation';
import { useUnApplyFromJobMutation } from '@worker/hooks/useUnApplyFromJobMutation';
import { useJobRecommendationById } from '@worker/hooks/useJobRecommendationById';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { HiOutlineCalendar } from 'react-icons/hi';
import { BsCurrencyDollar } from 'react-icons/bs';
import { getGoogleMapsUrl } from '@utils/JobUtils';
import { IoPersonCircleOutline } from 'react-icons/io5';
import { BiLinkExternal } from 'react-icons/bi';
import {
  JobIdSource,
  JobMutationAction,
  JobMutationState,
  TransformedJobItem,
} from '@worker/types/myJobs.type';
import {
  buildJobPageUrl,
  buildJobViewUrl,
  getRecommendationFlags,
  normalizeJobFromDTO,
} from '@worker/helpers/my-jobs-helpers';
import { formatLocalDate } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';
import { GENDERS, Gender } from '@constants/genderType';
import defaultAvatar from '@assets/default-avatar.png';
import UnApplyConfirmModal from '@worker/components/MyJobs/modals/UnApplyConfirmModal';
import JobActionsToolbar from '@worker/components/MyJobs/JobActionsToolbar/JobActionsToolbar';
import JobPictures from '@components/JobPictures/JobPictures';

const EMPTY_MUTATION: JobMutationState = {
  action: null,
  targetId: null,
};

export interface JobDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  /** Modal fetches the payload internally via `useJobRecommendationById`. */
  jobRecommendationId: number | null;
  onDismiss?: (recommendation: TransformedJobItem) => void;
  onBookmark?: (recommendation: TransformedJobItem) => void;
  onShare?: (recommendation: TransformedJobItem) => void;
  onMessage?: (recommendation: TransformedJobItem) => void;
  /** Intercept the apply action — e.g., to run pre-apply modals (profile/distance/questions). */
  onApply?: (recommendation: TransformedJobItem) => void;
  mutation?: JobMutationState;
}

function JobDetailsModal({
  opened,
  onClose,
  jobRecommendationId,
  onDismiss,
  onBookmark,
  onShare,
  onMessage,
  onApply,
  mutation = EMPTY_MUTATION,
}: JobDetailsModalProps) {
  const {
    data: recommendationData,
    isLoading,
    isError,
  } = useJobRecommendationById(jobRecommendationId, { enabled: opened });

  useEffect(() => {
    if (opened && isError) {
      onClose();
    }
  }, [opened, isError, onClose]);

  const recommendation = useMemo<TransformedJobItem | null>(
    () =>
      recommendationData
        ? normalizeJobFromDTO(recommendationData, JobIdSource.RECOMMENDATION)
        : null,
    [recommendationData]
  );

  const {
    job,
    jobAssignmentId,
    isApplied,
    isPastJob,
    isOffered,
    isHired,
    isCancelled,
  } = getRecommendationFlags(recommendation);

  const applyMutation = useApplyToJobMutation();
  const unApplyMutation = useUnApplyFromJobMutation({ onSuccess: onClose });
  const [unApplyModalOpen, setUnApplyModalOpen] = useState(false);

  const companyProfileUrl = job?.company.profileURLName
    ? `${import.meta.env.VITE_SERVER_BASE_URL}/companies/${job.company.profileURLName}`
    : undefined;

  const handleUnApplyClick = () => {
    setUnApplyModalOpen(true);
  };

  const handleUnApplyConfirm = () => {
    if (!job?.id) return;
    unApplyMutation.mutate(
      { jobId: job.id, jobName: job.title },
      {
        onSettled: () => {
          setUnApplyModalOpen(false);
        },
      }
    );
  };

  const handleUnApplyModalClose = () => {
    if (unApplyMutation.isPending) return;
    setUnApplyModalOpen(false);
  };

  const jobDetailsUrl = job ? buildJobPageUrl(job.publicUrl) : undefined;
  const payRateText = job
    ? `${job.payRate.toFixed(2)} / ${job.payRatePeriod.toLowerCase()}`
    : '';

  const handleViewJobPage = () => {
    const url = buildJobViewUrl(jobAssignmentId, job?.publicUrl);
    if (url) window.open(url, '_blank');
  };

  const isApplyLoading =
    (applyMutation.isPending && applyMutation.variables?.jobId === job?.id) ||
    (mutation.action === JobMutationAction.APPLY &&
      mutation.targetId === job?.id);

  const getActionButtonConfig = () => {
    if (isPastJob) {
      return { text: 'Past Job', variant: 'light' as const, disabled: true };
    }
    if (isCancelled) {
      return {
        text: 'Canceled',
        variant: 'filled' as const,
        color: 'yellow',
        notClickable: true,
      };
    }
    if (isOffered || isHired) {
      return {
        text: isOffered ? 'View Offer' : 'View Job',
        variant: 'filled' as const,
        onClick: handleViewJobPage,
      };
    }
    if (isApplied) {
      return {
        text: 'Un-Apply',
        variant: 'white' as const,
        onClick: handleUnApplyClick,
      };
    }
    return {
      text: "I'm Interested",
      onClick: () => {
        if (!recommendation || !job?.id) return;
        if (onApply) {
          onApply(recommendation);
        } else {
          applyMutation.mutate({ jobId: job.id, jobName: job.title });
        }
      },
      loading: isApplyLoading,
    };
  };

  const renderActionButton = () => {
    const config = getActionButtonConfig();
    return (
      <Button
        variant={config.variant}
        color={config.color}
        size="md"
        tt="capitalize"
        disabled={config.disabled}
        loading={config.loading}
        onClick={config.onClick}
        style={
          config.notClickable
            ? { cursor: 'not-allowed', opacity: 0.65 }
            : undefined
        }
      >
        {config.text}
      </Button>
    );
  };

  const toolbarOnDismiss =
    onDismiss && recommendation
      ? () => {
          onDismiss(recommendation);
          onClose();
        }
      : undefined;
  const toolbarOnBookmark =
    onBookmark && recommendation ? () => onBookmark(recommendation) : undefined;
  const toolbarOnShare =
    onShare && recommendation ? () => onShare(recommendation) : undefined;
  const toolbarOnMessage =
    onMessage && recommendation
      ? () => {
          onMessage(recommendation);
          onClose();
        }
      : undefined;

  const showToolbar =
    !!recommendation &&
    (!!toolbarOnDismiss ||
      !!toolbarOnBookmark ||
      !!toolbarOnShare ||
      !!toolbarOnMessage);

  return (
    <Modal
      opened={opened && !isError}
      onClose={onClose}
      size={isMobile ? '100%' : 'xl'}
      fullScreen={isMobile}
      title={
        job ? (
          <Stack gap="xs">
            <Text size={isMobile ? 'lg' : 'xl'} fw="700" c="dimmed">
              {job.eventName || 'Job Details'}
            </Text>
            {jobDetailsUrl && !isPastJob && (
              <Anchor
                href={jobDetailsUrl}
                c="black"
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <BiLinkExternal size={14} />
                Open in New Tab
              </Anchor>
            )}
          </Stack>
        ) : undefined
      }
    >
      {isLoading && !job && (
        <Center py="xl">
          <Loader />
        </Center>
      )}
      {job && recommendation && (
        <Stack gap="md">
          {/* Header Section */}
          <Flex align="flex-start" gap={isMobile ? 'sm' : 'md'}>
            <Anchor
              href={companyProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ flexShrink: 0 }}
            >
              <Avatar
                src={job.company.profileImageUrl || defaultAvatar}
                size={isMobile ? 60 : 80}
                radius="md"
              />
            </Anchor>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              {/* Title and Rating Row */}
              <Flex justify="space-between" align="flex-start" gap="md">
                <Title
                  order={isMobile ? 5 : 3}
                  style={{ wordBreak: 'break-word' }}
                >
                  {job.title}
                </Title>
                <Rating
                  value={job.company.rating || 0}
                  readOnly
                  fractions={2}
                  color="var(--yellow)"
                  size={isMobile ? 'sm' : 'md'}
                  style={{ flexShrink: 0 }}
                />
              </Flex>

              {/* Company/Brand and Rating Text Row */}
              <Flex justify="space-between" align="center" gap="md" wrap="wrap">
                <Flex align="center" gap="xs" wrap="wrap">
                  <Anchor
                    href={companyProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    fw={500}
                    size={isMobile ? 'sm' : 'md'}
                    c="inherit"
                  >
                    {job.company.name}
                  </Anchor>
                  {job.brandRepresented && (
                    <>
                      <Text size="sm">Brand:</Text>
                      <Badge variant="light" color="blue">
                        {job.brandRepresented}
                      </Badge>
                    </>
                  )}
                </Flex>
                <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
                  <Text size="sm" fw={500}>
                    {(job.company.rating || 0).toFixed(2)} Overall
                  </Text>
                  {job.company.reviewsCount != null && (
                    <Text size="sm" c="dimmed">
                      ({job.company.reviewsCount} Reviews)
                    </Text>
                  )}
                </Flex>
              </Flex>

              {/* Job Details - Horizontal Row */}
              <Flex align="center" gap="md" wrap="wrap" mt="xs">
                <Flex align="center" gap={4}>
                  <HiOutlineCalendar size={18} />
                  <Text size="sm">
                    {formatLocalDate(
                      new Date(job.startDate),
                      DATE_FORMATS.DATE_FORMAT
                    )}{' '}
                    -{' '}
                    {formatLocalDate(
                      new Date(job.endDate),
                      DATE_FORMATS.DATE_FORMAT
                    )}
                  </Text>
                </Flex>

                <Flex align="center" gap={4}>
                  <FaMapMarkerAlt size={16} />
                  {!job.nationalJob && job.address?.googleAddress ? (
                    <Anchor
                      href={getGoogleMapsUrl(job.address.googleAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      style={{ color: 'inherit' }}
                      underline="never"
                    >
                      {job.address.googleAddress}
                    </Anchor>
                  ) : (
                    <Text size="sm">
                      {job.nationalJob
                        ? 'National Job'
                        : job.address?.googleAddress}
                    </Text>
                  )}
                </Flex>

                <Flex align="center" gap={4}>
                  <BsCurrencyDollar size={18} />
                  <Text size="sm">{payRateText}</Text>
                </Flex>

                {job.openPositions && (
                  <Flex align="center" gap={4}>
                    <IoPersonCircleOutline size={18} />
                    <Text size="sm">
                      {job.openPositions}{' '}
                      {job.openPositions === 1 ? 'position' : 'positions'}{' '}
                      available
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Stack>
          </Flex>

          <Divider />

          {/* Job Description */}
          {(job.description || job.pictures?.length) && (
            <Stack gap="xs">
              {job.description && (
                <>
                  <Title order={5}>Description</Title>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {job.description}
                  </Text>
                </>
              )}
              <JobPictures pictures={job.pictures} />
            </Stack>
          )}

          {/* Skills and Requirements */}
          {job.skills?.length > 0 &&
            (() => {
              const nonGenderSkills = job.skills.filter(
                (skill) => !GENDERS.includes(skill)
              );
              const genderSkill = job.skills.find((skill) =>
                GENDERS.includes(skill)
              );
              const genderLabel: Record<string, string> = {
                [Gender.ANY_GENDER]: 'Any Gender',
                [Gender.MALE]: 'Male Preferred',
                [Gender.FEMALE]: 'Female Preferred',
              };
              return (
                <Stack gap="xs">
                  <Title order={5}>Skills and Requirements</Title>
                  {nonGenderSkills.length > 0 && (
                    <List size="sm" spacing="xs" withPadding>
                      {nonGenderSkills.map((skill) => (
                        <List.Item key={skill}>{skill}</List.Item>
                      ))}
                    </List>
                  )}
                  {genderSkill && (
                    <Text size="sm">
                      <b>Gender:</b> {genderLabel[genderSkill]}
                    </Text>
                  )}
                </Stack>
              );
            })()}

          {/* Job Tags */}
          <Flex gap="xs">
            {job.nationalJob && <Badge variant="light">National Job</Badge>}
            {job.ongoingJob && <Badge variant="light">Ongoing</Badge>}
          </Flex>

          {/* Action Toolbar + Main Action Button */}
          <Flex
            gap="md"
            direction={isMobile ? 'column' : 'row'}
            justify="end"
            align={isMobile ? 'stretch' : 'center'}
            wrap="wrap"
          >
            {showToolbar && (
              <JobActionsToolbar
                recommendation={recommendation}
                mutation={mutation}
                onDismiss={toolbarOnDismiss}
                onBookmark={toolbarOnBookmark}
                onShare={toolbarOnShare}
                onMessage={toolbarOnMessage}
                isPastJob={isPastJob}
              />
            )}
            {renderActionButton()}
          </Flex>

          <UnApplyConfirmModal
            opened={unApplyModalOpen}
            onClose={handleUnApplyModalClose}
            onConfirm={handleUnApplyConfirm}
            jobTitle={job.title}
            isLoading={unApplyMutation.isPending}
          />
        </Stack>
      )}
    </Modal>
  );
}

export default JobDetailsModal;
